import React, { useEffect, useState, useRef } from 'react';
import { BanIcon, ArrowLeftIcon, XIcon, PrinterIcon } from '../ui/icons';
import { IconButton, panelTopHairline } from '../ui/common';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from '../translations';
import { getInventorySettingsStorageKey } from '../utils/inventoryStorage';
import { API_BASE } from '../config';
import { Banner } from '../components/ui/Banner';
import { scrollToTop } from '../utils/scrollToTop';
import useBarcodeScan from '../utils/useBarcodeScan';
import ScanSources from '../components/scanner/ScanSources';
import { typeLabel, classLabel, equipmentStatusLabel } from '../utils/dictionaryLabel';
import { zapiszZPrzeniesieniemNumeru } from '../utils/przeniesienieNumeru';
import { useConfirm } from '../components/ui/Modal';

// SVG Icons
const EyeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SaveIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20,6 9,17 4,12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AlertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DotIcon = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  display: inline-block;
`;

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: clamp(12px, 2vw, 32px);
  background: var(--panel-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-color);
  color: var(--text);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
  animation: ${slideIn} 0.6s ease-out;
  min-height: calc(100vh - 48px);

  ${panelTopHairline}

  @media (min-width: 1200px) {
    max-width: 900px;
  }

@media (max-width: 768px) {
  padding: 16px;
  border-radius: 12px;
  min-height: auto;
}

/* :not([data-nav-button]) wylacza strzalke powrotu w naglowku z tej reguly — bez tego
     przycisk nawigacyjny (majacy wygladac identycznie jak na kazdej innej podstronie)
     dostawalby fioletowa obwodke/cien w trybie edycji, tak jak realne pola formularza. */
  &[data-editing="true"] input,
  &[data-editing="true"] select,
  &[data-editing="true"] button:not([data-nav-button]) {
    border-color: rgba(99,102,241,0.35);
    box-shadow: var(--shadow-md);
    transition: box-shadow .18s, border-color .18s;
  }

  &[data-editing="true"] .readonly {
    opacity: 0.65;
    background: transparent;
    border-color: transparent;
    color: var(--text-muted);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
`;

const TitleSection = styled.div`
  flex: 1;
  text-align: center;
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  font-size: 2rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -0.025em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

`;

const TitleIcon = styled.div`
  color: var(--primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--text-muted);
  font-weight: 400;
`;

const DetailPanel = styled.div`
  background: var(--card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 32px;
  box-sizing: border-box;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px var(--shadow-color);
    border-color: var(--input-border);
  }
`;

const StatusSection = styled.div`
  padding: 0;
  background: transparent;
  border: none;
  margin-bottom: 24px;
`;

const StatusLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-muted);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CurrentStatusDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--input-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  margin-bottom: 16px;
  
  .label {
    font-size: 0.9rem;
    color: var(--text-muted);
    font-weight: 500;
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatusOption = styled.button`
  padding: 16px 20px;
  border: 2px solid ${props => props.selected ? 'var(--primary)' : 'var(--input-border)'};
  border-radius: 12px;
  background: ${props => props.selected ? 
    'rgba(139, 92, 246, 0.1)' : 
    'var(--input-bg)'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  font-weight: ${props => props.selected ? '600' : '500'};
  color: var(--text);
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  
  .status-name {
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${props => props.selected ? 'var(--primary)' : 'var(--text)'};
  }
  
  .status-description {
    font-size: 0.8rem;
    color: var(--text-muted);
    font-weight: 400;
    line-height: 1.4;
  }
  
  &:hover:not(:disabled) {
    border-color: var(--primary);
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 12px;
    right: 12px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid ${props => props.selected ? 'var(--primary)' : 'var(--input-border)'};
    background: ${props => props.selected ? 'var(--primary)' : 'transparent'};
    transition: all 0.2s ease;
  }
  
  &::before {
    content: '✓';
    position: absolute;
    top: 15px;
    right: 16px;
    font-size: 12px;
    color: white;
    opacity: ${props => props.selected ? '1' : '0'};
    transition: opacity 0.2s ease;
  }
`;

const InfoBox = styled.div`
  padding: 16px;
  background: ${props => props.type === 'warning' ?
    'var(--warning-bg)' :
    'var(--info-bg)'};
  border: 1px solid ${props => props.type === 'warning' ?
    'var(--warning-border)' :
    'var(--info-border)'};
  border-radius: 12px;
  color: ${props => props.type === 'warning' ? 'var(--warning)' : 'var(--info)'};
  font-size: 0.85rem;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  line-height: 1.5;
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const StatusChangeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--panel-bg);
  border-radius: 8px;
  margin-top: 16px;
  font-size: 0.85rem;
  color: var(--text-muted);
  
  .arrow {
    color: var(--primary);
    font-weight: bold;
  }
`;

function FilterDropdown({ value, onChange, options, placeholder }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef(null);
  
  const close = () => setOpen(false);

  React.useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener('mousedown', h);
    document.addEventListener('scroll', h, true);
    const onWheel = (e) => {
      const t = e.target;
      if (t && t.tagName === 'INPUT' && t.type === 'number' && ref.current && ref.current.contains(t)) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => { 
      document.removeEventListener('mousedown', h); 
      document.removeEventListener('scroll', h, true); 
      document.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [open]);

  const selected = options.find(o => o.value === value);
  const filteredOptions = options.filter(o => {
    if (!query) return true;
    return String(o.label || '').toLowerCase().includes(String(query).toLowerCase());
  });
  
  return (
    <DropdownWrapper ref={ref}>
      <FilterDropdownButton
        type="button"
        data-open={open}
        data-placeholder={!selected}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!open) setQuery('');
          setOpen(o => !o);
        }}
      >
        <span>{selected ? selected.label : placeholder}</span>
        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </FilterDropdownButton>
      {open && (
        <FilterOptionsList role="listbox">
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--input-border)' }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Wpisz, aby filtrować…"
              autoFocus
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid var(--input-border)',
                borderRadius: 8,
                background: 'var(--input-bg)',
                color: 'var(--text)'
              }}
            />
          </div>
          {filteredOptions.map(o => {
            const sel = o.value === value;
            return (
              <FilterOptionItem
                key={o.value}
                data-selected={sel}
                onClick={() => { onChange(o.value); close(); }}
              >
                <span className="option-content">
                  {sel && <DotIcon />}
                  <span className="option-label">{o.label}</span>
                </span>
              </FilterOptionItem>
            );
          })}
          {filteredOptions.length === 0 && (
            <div style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>Brak dopasowań</div>
          )}
        </FilterOptionsList>
      )}
    </DropdownWrapper>
  );
}

export default function EquipmentDetails() {
  const TYPEAHEAD_MIN_LENGTH = 2;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [equipment, setEquipment] = useState(null);
  const { currencies, loading: currenciesLoading, fetchCurrencies } = useCurrencyList();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ price_over_7: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inventorySettings, setInventorySettings] = useState(loadInventorySettings());
  // currency autocomplete state (used in edit mode)
  const [currencySuggestions, setCurrencySuggestions] = useState([]);
  const [showCurrencySuggestions, setShowCurrencySuggestions] = useState(false);
  const currencyContainerRef = useRef(null);
  const [fieldSuggestionIndex, setFieldSuggestionIndex] = useState({});
  const [activeTypeahead, setActiveTypeahead] = useState({ field: '', items: [] });
  
  // Stan dla statusu i rezerwacji
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isRented, setIsRented] = useState(false);
  const [hasFutureReservations, setHasFutureReservations] = useState(false); // DODANE
  const [futureReservationsCount, setFutureReservationsCount] = useState(0); // DODANE

  const statusOptions = [
    { value: 'Dostępny', label: t('inventory.status.Dostępny') || 'Dostępny' },
    { value: 'Wypożyczony', label: t('inventory.status.Wypożyczony') || 'Wypożyczony' },
    { value: 'W serwisie', label: t('inventory.status.W serwisie') || 'W serwisie' },
    { value: 'Nieaktywny', label: t('inventory.status.Nieaktywny') || 'Nieaktywny' }
  ];

  useEffect(() => {
    fetchEquipmentDetails();
  }, [id]);

  useEffect(() => {
    axios.get(`${API_BASE}/api/classes`, { withCredentials: true })
      .then(res => setClasses(res.data))
      .catch(() => setClasses([]));

    // podpowiedzi z istniejących rekordów
    axios.get(`${API_BASE}/api/equipment`, { withCredentials: true })
      .then(res => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const suggestionFields = [
          'classId',
          'brand',
          'location',
          'supplier'
        ];
        const sets = suggestionFields.reduce((acc, key) => {
          acc[key] = new Set();
          return acc;
        }, {});

        rows.forEach(row => {
          const meta = row && typeof row.metadata === 'object' && row.metadata ? row.metadata : {};
          suggestionFields.forEach(key => {
            const fromTop = key === 'classId'
              ? (row ? (row.class ?? row.class_name) : undefined)
              : (row ? row[key] : undefined);
            const fromMeta = meta[key];
            const value = fromTop ?? fromMeta;
            if (Array.isArray(value)) {
              value.forEach(v => {
                const s = String(v || '').trim();
                if (s) sets[key].add(s);
              });
            } else {
              const s = String(value ?? '').trim();
              if (s) sets[key].add(s);
            }
          });
        });

        const next = {};
        Object.keys(sets).forEach(key => {
          next[key] = Array.from(sets[key]).slice(0, 100);
        });
        setFieldSuggestionIndex(next);
      })
      .catch(() => setFieldSuggestionIndex({}));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const settingsKey = getInventorySettingsStorageKey();
    const onStorage = (e) => {
      if (e.key === settingsKey) {
        setInventorySettings(loadInventorySettings());
      }
    };
    window.addEventListener('storage', onStorage);
    setInventorySettings(loadInventorySettings());
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const fetchEquipmentDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/equipment/${id}`, { withCredentials: true });
      setEquipment(res.data);
      setForm({ ...res.data, price_over_7: res.data.price_over_7 || '', classId: res.data.class || res.data.classId || '' });
      setSelectedStatus(res.data.status);
      
      // ZAKTUALIZOWANE: Sprawdź statusy rezerwacji
      try {
        const rentedRes = await axios.get(`${API_BASE}/api/equipment/${id}/rental-status`, { withCredentials: true });
        setIsRented(rentedRes.data.isRented || false);
        setHasFutureReservations(rentedRes.data.hasFutureReservations || false);
        setFutureReservationsCount(rentedRes.data.futureReservationsCount || 0);
        
        console.log('Status rezerwacji:', {
          isRented: rentedRes.data.isRented,
          hasFutureReservations: rentedRes.data.hasFutureReservations,
          count: rentedRes.data.futureReservationsCount
        });
      } catch (err) {
        console.log('Błąd sprawdzania rezerwacji:', err);
        setIsRented(false);
        setHasFutureReservations(false);
        setFutureReservationsCount(0);
      }
      
      setLoading(false);
    } catch (err) {
      setError(t('equipmentDetails.loadError') || 'Nie udało się pobrać danych sprzętu.');
      scrollToTop();
      setLoading(false);
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const closeTypeahead = () => setActiveTypeahead({ field: '', items: [] });

  const getTypeaheadValues = (field) => {
    if (field === 'classId') {
      return Array.from(new Set([
        ...((fieldSuggestionIndex.classId || []).map(v => String(v || '').trim()).filter(Boolean)),
        ...((classes || []).map(cls => String(cls?.name || '').trim()).filter(Boolean))
      ]));
    }
    return Array.from(new Set(((fieldSuggestionIndex[field] || []).map(v => String(v || '').trim()).filter(Boolean))));
  };

  const updateTypeahead = (field, value) => {
    const q = String(value || '').trim().toLowerCase();
    if (q.length < TYPEAHEAD_MIN_LENGTH) {
      closeTypeahead();
      return;
    }
    const matches = getTypeaheadValues(field)
      .filter(item => item.toLowerCase().startsWith(q))
      .slice(0, 8);
    setActiveTypeahead({ field, items: matches });
  };

  const handleTypeaheadChange = (e) => {
    handleChange(e);
    updateTypeahead(normalizeDetailFieldKey(e.target.name), e.target.value);
  };

  // Czytnik kodów — tylko w trybie edycji: w podglądzie nie ma czego wypełniać, a skan
  // miałby wtedy zmieniać dane bez wejścia w edycję. Cel jak w formularzu dodawania:
  // pole kodu kreskowego/QR, a gdy organizacja go nie używa — numer inwentarzowy.
  // Tu NIE trzeba blokować Entera: ta strona nie jest formularzem z przyciskiem submit,
  // więc znak kończący skan nie ma czego wysłać.
  // `dynamicFieldKeys` powstaje dopiero po wczytaniu sprzętu, czyli PO wcześniejszych
  // `return`-ach — hook nie może się na nim opierać. Konfiguracja kolumn jest tu już pod ręką
  // i mówi dokładnie to samo: czy organizacja w ogóle używa pola na kod.
  const scanTargetField = (inventorySettings?.visibleColumns || []).includes('barcode')
    ? 'barcode'
    : 'inventory_number';

  // Czytnik USB, aparat i telefon (ScanSources w trybie edycji) wpisują kod w to samo pole.
  const handleScanFill = (kod) => {
    setForm(prev => ({ ...prev, [scanTargetField]: kod }));
    setFieldErrors(prev => ({ ...prev, [scanTargetField]: false }));
    return { tone: 'ok', text: t('addEquipment.scanFilled').replace('{code}', kod) };
  };
  useBarcodeScan(handleScanFill, { enabled: edit });

  const handleTypeaheadSelect = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => ({ ...prev, [field]: false }));
    closeTypeahead();
  };

  const renderTypeahead = (field) => (
    activeTypeahead.field === field && activeTypeahead.items.length > 0 ? (
      <TypeaheadList>
        {activeTypeahead.items.map(item => (
          <TypeaheadItem
            type="button"
            key={`${field}-${item}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleTypeaheadSelect(field, item)}
          >
            <DotIcon />
            <span>{item}</span>
          </TypeaheadItem>
        ))}
      </TypeaheadList>
    ) : null
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onMouseDown = (e) => {
      if (e.target?.closest?.('[data-equipment-typeahead="true"]')) return;
      closeTypeahead();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      console.log('=== ROZPOCZĘCIE ZAPISU ===');
      console.log('ID sprzętu:', id);
      console.log('Wybrany status:', selectedStatus);
      console.log('Oryginalny status:', equipment.status);
      
      // Jeśli status się zmienił, zapisz go
      if (selectedStatus !== equipment.status) {
        try {
          console.log('Wysyłam żądanie PATCH do:', `${API_BASE}/api/equipment/${id}/status`);
          console.log('Payload:', JSON.stringify({ status: selectedStatus }));

          const csrf = localStorage.getItem('csrfToken') || '';
          const statusResponse = await axios.patch(
            `${API_BASE}/api/equipment/${id}/status`,
            { status: selectedStatus },
            {
              withCredentials: true,
              headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf }
            }
          );
          
          console.log('✅ Odpowiedź serwera (status):', statusResponse.data);
          
          // Jeśli backend automatycznie zmienił status
          if (statusResponse.data.autoStatus) {
            setSelectedStatus(statusResponse.data.autoStatus);
            setError(statusResponse.data.message);
            scrollToTop();
            setSuccess('');
            setLoading(false);
            return;
          }
        } catch (statusErr) {
          console.error('❌ Błąd zmiany statusu:', statusErr);
          console.error('Response data:', statusErr.response?.data);
          console.error('Response status:', statusErr.response?.status);
          console.error('Response headers:', statusErr.response?.headers);
          
          if (statusErr.response?.status === 409) {
            const errMsg = statusErr.response?.data?.message || 
                          t('inventory.cannotDeactivateRented') || 
                          'Nie można zmienić statusu - sprzęt jest wypożyczony lub ma przyszłe rezerwacje';
            setError(errMsg);
            scrollToTop();
            setSuccess('');
            setLoading(false);
            return;
          }
          
          if (statusErr.response?.status === 400) {
            setError(statusErr.response?.data?.message || 'Nieprawidłowy status');
            scrollToTop();
            setSuccess('');
            setLoading(false);
            return;
          }
          
          throw statusErr;
        }
      }
      
      console.log('Zapisuję pozostałe dane sprzętu...');
      
      // Zapisz pozostałe dane
      // Przygotuj payload: podstawowe kolumny + `metadata` z opcjonalnymi polami
      const coreKeys = new Set(['name','size','brand','price','price_over_7','bail','inventory_number','classId','typeId','category']);
      // Start from existing metadata (server) and any nested form.metadata
      const existingMeta = (equipment && equipment.metadata && typeof equipment.metadata === 'object') ? equipment.metadata : {};
      const nestedFormMeta = (form && form.metadata && typeof form.metadata === 'object') ? form.metadata : {};
      const metadata = { ...existingMeta, ...nestedFormMeta };

      Object.keys(form || {}).forEach(k => {
        if (k === 'metadata') return;
        if (!coreKeys.has(k)) {
          const v = form[k];
          if (hasMeaningfulValue(v)) metadata[k] = v;
          // if field cleared in top-level form, preserve existing metadata value (do not delete)
        }
      });

      const normalizedClassValue = String(form.classId || '').trim();
      const matchedClass = (classes || []).find(cls => String(cls?.name || '').trim().toLowerCase() === normalizedClassValue.toLowerCase());

      const putPayload = {
        name: form.name,
        size: form.size || null,
        brand: form.brand || null,
        price: form.price,
        price_over_7: form.price_over_7,
        bail: form.bail,
        inventory_number: form.inventory_number,
        classId: matchedClass ? matchedClass.id : normalizedClassValue,
        typeId: form.typeId,
        category: form.category || null,
        metadata: Object.keys(metadata).length ? metadata : null
      };

      const csrf = localStorage.getItem('csrfToken') || '';
      // Numer nieaktywnej sztuki: okno „Przenieść numer?” i ponowny zapis (utils/przeniesienieNumeru.js).
      await zapiszZPrzeniesieniemNumeru((dodatkowe) => axios.put(`${API_BASE}/api/equipment/${id}`, { ...putPayload, ...dodatkowe }, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrf }
      }), { confirm, t });
      setEdit(false);
      
      const updatedEquipment = { ...form, status: selectedStatus, metadata: putPayload.metadata || {} };
      updatedEquipment.class = matchedClass ? matchedClass.name : normalizedClassValue;
      setEquipment(updatedEquipment);

      setSuccess(t('equipmentDetails.saveSuccess') || 'Zmiany zapisane!');
      setError('');
      setTimeout(() => {
        navigate('/inventory');
      }, 800);
    } catch (err) {
      console.error('❌ Błąd zapisu (główny catch):', err);
      console.error('Response:', err.response?.data);
      const msg = err.response?.data?.message || t('equipmentDetails.saveError') || 'Błąd podczas zapisu zmian.';
      setError(msg);
      scrollToTop();
      setSuccess('');
      // Parse error to highlight fields
      const msgLower = msg.toLowerCase();
      const errors = {};
      if (/nazwa|name/.test(msgLower)) errors.name = true;
      if (/rozmiar|size/.test(msgLower)) errors.size = true;
      if (/marka|brand/.test(msgLower)) errors.brand = true;
      if (/cena|price/.test(msgLower)) errors.price = true;
      if (/kaucja|bail/.test(msgLower)) errors.bail = true;
      if (/numer|inventory/.test(msgLower)) errors.inventory_number = true;
      if (/kategoria|category/.test(msgLower)) errors.category = true;
      if (/klasa|class/.test(msgLower)) errors.classId = true;
      if (/typ|type/.test(msgLower)) errors.typeId = true;
      setFieldErrors(errors);
      scrollToTop();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEdit(false);
    setSelectedStatus(equipment.status); // Resetuj status do oryginalnego
    setForm({ ...equipment, price_over_7: equipment.price_over_7 || '', classId: equipment.class || equipment.classId || '' });
  };

  if (error && !equipment) return <Container><Title>{t('common.error') || 'Błąd'}</Title><Value style={{ color: 'red' }}>{error}</Value></Container>;
  if (loading && !equipment) return <LoadingSpinner />;
  if (!equipment) return <Container><Title>{t('common.loading') || 'Ładowanie...'}</Title></Container>;

  const categoryName = String(form.category || equipment.category || '').trim();
  const configuredCategoryFieldsRaw = inventorySettings?.categoryRequirements?.[categoryName] || [];
  const configuredCategoryFields = Array.from(new Set(
    (Array.isArray(configuredCategoryFieldsRaw) ? configuredCategoryFieldsRaw : [])
      .map(normalizeDetailFieldKey)
      .filter(key => {
        if (!key) return false;
        const lower = String(key).toLowerCase();
        if (excludedDynamicFields.has(key) || excludedDynamicFields.has(lower)) return false;
        if (lower.endsWith('id')) return false;
        return true;
      })
  ));

  

  // Include keys from equipment.metadata and form.metadata when detecting used fields
  const mergedSourceForKeys = {
    ...(equipment || {}),
    ...((equipment && equipment.metadata && typeof equipment.metadata === 'object') ? equipment.metadata : {}),
    ...(form || {}),
    ...((form && form.metadata && typeof form.metadata === 'object') ? form.metadata : {})
  };

  const usedEquipmentFields = Array.from(new Set(
    Object.keys(mergedSourceForKeys)
      .map(normalizeDetailFieldKey)
      .filter(key => {
        if (!key) return false;
        const lower = String(key).toLowerCase();
        if (excludedDynamicFields.has(key) || excludedDynamicFields.has(lower)) return false;
        if (lower.endsWith('id')) return false;
        return true;
      })
      .filter(key => hasMeaningfulValue(getFieldValue(form, key)) || hasMeaningfulValue(getFieldValue(equipment, key)))
  ));

  // Kolumny własne organizacji: zawsze edytowalne, także gdy ta sztuka ma je jeszcze puste.
  // To ta sama zasada, co w formularzu dodawania — skoro firma założyła pole, ma je czym
  // wypełnić w obu miejscach, nie tylko przy zakładaniu sprzętu.
  const customFieldDefs = Array.isArray(inventorySettings?.customColumns) ? inventorySettings.customColumns : [];
  const customFieldDefByKey = new Map(customFieldDefs.map(c => [c.key, c]));

  const dynamicFieldKeys = Array.from(new Set([
    ...configuredCategoryFields,
    ...usedEquipmentFields,
    ...customFieldDefs.map(c => c.key)
  ]));

  return (
    <Container data-editing={edit}>
      <Header>
        <IconButton
          onClick={() => navigate('/inventory')}
          aria-label={t('addEquipment.backToInventory') || 'Powrót do magazynu'}
          data-nav-button="true"
        >
          <ArrowLeftIcon />
        </IconButton>
        <TitleSection>
          <Title>
            <TitleIcon><EyeIcon /></TitleIcon>
            {t('equipmentDetails.title') || 'Szczegóły sprzętu'}
          </Title>
          <Subtitle>{t('equipmentDetails.subtitle') || 'Przeglądaj lub edytuj informacje o sprzęcie'}</Subtitle>
        </TitleSection>
      </Header>
      
      {success && (
        <div style={{ marginBottom: 20 }}><Banner tone="success">{success}</Banner></div>
      )}
      {error && !success && (
        <div style={{ marginBottom: 20 }}><Banner tone="danger">{error}</Banner></div>
      )}

      <DetailPanel>

        {edit ? (
          <>
            {/* SEKCJA ZMIANY STATUSU - TYLKO W TRYBIE EDYCJI */}
            <StatusSection>
              <StatusLabel>{t('inventory.changeStatus') || 'Zmiana statusu'}</StatusLabel>
              
              <CurrentStatusDisplay>
                <span className="label">{t('inventory.currentStatus') || 'Aktualny status'}:</span>
                <StatusBadge data-status={equipment.status?.toLowerCase()}>
                  <DotIcon />
                  {equipmentStatusLabel(t, equipment.status)}
                </StatusBadge>
              </CurrentStatusDisplay>

              {/* NOWY KOMUNIKAT: Sprzęt zarezerwowany (obecnie lub w przyszłości) */}
              {(isRented || hasFutureReservations) && (
                <InfoBox type="warning">
                  <AlertIcon />
                  <div>
                    <strong><BanIcon size={16} /> {t('inventory.cannotChangeStatus') || 'Nie można zmienić statusu'}</strong>
                    <br />
                    {isRented && hasFutureReservations ? (
                      t('inventory.rentedAndFutureReservations') || 
                      'Sprzęt jest obecnie wypożyczony i ma zaplanowane przyszłe rezerwacje. Zmiana statusu będzie możliwa dopiero po zakończeniu wszystkich rezerwacji.'
                    ) : isRented ? (
                      t('inventory.currentlyRented') || 
                      'Sprzęt jest obecnie wypożyczony. Zmiana statusu będzie możliwa dopiero po zwrocie przez klienta.'
                    ) : (
                      <>
                        {t('inventory.hasFutureReservationsBlocked') || 
                         `Sprzęt ma zaplanowane przyszłe rezerwacje (${futureReservationsCount}). Zmiana statusu będzie możliwa po anulowaniu rezerwacji lub ich zakończeniu.`}
                      </>
                    )}
                  </div>
                </InfoBox>
              )}

              {/* Komunikat o zmianie statusu - tylko gdy NIE MA blokad */}
              {selectedStatus !== equipment.status && !isRented && !hasFutureReservations && (
                <InfoBox type="info">
                  <DotIcon />
                  <div>
                    <strong>{t('inventory.statusWillChange') || 'Status zostanie zmieniony'}</strong>
                    <StatusChangeInfo>
                      <StatusBadge data-status={equipment.status?.toLowerCase()}>
                        <DotIcon />
                        {equipmentStatusLabel(t, equipment.status)}
                      </StatusBadge>
                      <span className="arrow">→</span>
                      <StatusBadge data-status={selectedStatus?.toLowerCase()}>
                        <DotIcon />
                        {equipmentStatusLabel(t, selectedStatus)}
                      </StatusBadge>
                    </StatusChangeInfo>
                  </div>
                </InfoBox>
              )}

              <StatusGrid>
                {/* WSZYSTKIE opcje statusu - zablokowane gdy isRented LUB hasFutureReservations */}
                <StatusOption
                  selected={selectedStatus === 'Dostępny'}
                  disabled={equipment.status === 'Dostępny' || isRented || hasFutureReservations}
                  onClick={() => !isRented && !hasFutureReservations && equipment.status !== 'Dostępny' && setSelectedStatus('Dostępny')}
                  data-disabled={equipment.status === 'Dostępny' || isRented || hasFutureReservations}
                >
                  <div className="status-name">
                    <DotIcon style={{ color: 'var(--success)' }} />
                    {t('inventory.status.Dostępny') || 'Dostępny'}
                  </div>
                  <div className="status-description">
                    {t('inventory.statusDesc.available') || 'Sprzęt gotowy do wypożyczenia'}
                  </div>
                  {equipment.status === 'Dostępny' && (
                    <div className="status-hint" style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: '4px' }}>
                      ✓ {t('inventory.currentStatus')}
                    </div>
                  )}
                </StatusOption>

                <StatusOption
                  selected={selectedStatus === 'Wypożyczony'}
                  disabled={equipment.status === 'Wypożyczony' || isRented || hasFutureReservations}
                  onClick={() => !isRented && !hasFutureReservations && equipment.status !== 'Wypożyczony' && setSelectedStatus('Wypożyczony')}
                  data-disabled={equipment.status === 'Wypożyczony' || isRented || hasFutureReservations}
                >
                  <div className="status-name">
                    <DotIcon style={{ color: 'var(--primary)' }} />
                    {t('inventory.status.Wypożyczony') || 'Wypożyczony'}
                  </div>
                  <div className="status-description">
                    {t('inventory.statusDesc.rented') || 'Obecnie w użyciu przez klienta'}
                  </div>
                  {equipment.status === 'Wypożyczony' && (
                    <div className="status-hint" style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '4px' }}>
                      ✓ {t('inventory.currentStatus')}
                    </div>
                  )}
                </StatusOption>

                <StatusOption
                  selected={selectedStatus === 'W serwisie'}
                  disabled={equipment.status === 'W serwisie' || isRented || hasFutureReservations}
                  onClick={() => !isRented && !hasFutureReservations && equipment.status !== 'W serwisie' && setSelectedStatus('W serwisie')}
                  data-disabled={equipment.status === 'W serwisie' || isRented || hasFutureReservations}
                >
                  <div className="status-name">
                    <DotIcon style={{ color: 'var(--warning)' }} />
                    {t('inventory.status.W serwisie') || 'W serwisie'}
                  </div>
                  <div className="status-description">
                    {t('inventory.statusDesc.service') || 'W naprawie lub konserwacji'}
                  </div>
                  {equipment.status === 'W serwisie' && (
                    <div className="status-hint" style={{ fontSize: '0.75rem', color: 'var(--warning)', marginTop: '4px' }}>
                      ✓ {t('inventory.currentStatus')}
                    </div>
                  )}
                </StatusOption>

                <StatusOption
                  selected={selectedStatus === 'Nieaktywny'}
                  disabled={equipment.status === 'Nieaktywny' || isRented || hasFutureReservations}
                  onClick={() => !isRented && !hasFutureReservations && equipment.status !== 'Nieaktywny' && setSelectedStatus('Nieaktywny')}
                  data-disabled={equipment.status === 'Nieaktywny' || isRented || hasFutureReservations}
                >
                  <div className="status-name">
                    <DotIcon style={{ color: 'var(--text-muted)' }} />
                    {t('inventory.status.Nieaktywny') || 'Nieaktywny'}
                  </div>
                  <div className="status-description">
                    {t('inventory.statusDesc.inactive') || 'Wycofany z obiegu'}
                  </div>
                  {equipment.status === 'Nieaktywny' && (
                    <div className="status-hint" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                      ✓ {t('inventory.currentStatus')}
                    </div>
                  )}
                </StatusOption>
              </StatusGrid>
            </StatusSection>

            {/* GŁÓWNY GRID — najpierw żądana kolejność pól */}
            <FieldGrid>
              <Field>
                <Label>{t('addEquipment.category') || 'Kategoria sprzętu'}</Label>
                <Input name="category" value={form.category || ''} onChange={handleChange} readOnly className="readonly" />
              </Field>
              <Field>
                <Label>{t('addEquipment.brand') || 'Marka'}</Label>
                <TypeaheadWrapper data-equipment-typeahead="true">
                  <Input name="brand" value={form.brand || ''} autoComplete="off" onChange={handleTypeaheadChange} onFocus={(e) => updateTypeahead('brand', e.target.value)} data-error={!!fieldErrors.brand} />
                  {renderTypeahead('brand')}
                </TypeaheadWrapper>
              </Field>
              <Field>
                <Label>{t('addEquipment.name') || 'Nazwa'}</Label>
                <Input name="name" value={form.name || ''} autoComplete="off" onChange={handleChange} data-error={!!fieldErrors.name} />
              </Field>
              <Field>
                <Label>{t('addEquipment.size') || 'Rozmiar'}</Label>
                <Input name="size" value={form.size || ''} autoComplete="off" onChange={handleChange} data-error={!!fieldErrors.size} />
              </Field>
              <Field>
                <Label>{t('addEquipment.inventoryNumber') || 'Numer'}</Label>
                <Input name="inventory_number" value={form.inventory_number || ''} autoComplete="off" onChange={handleChange} data-error={!!fieldErrors.inventory_number} />
              </Field>
              <Field>
                <Label>{t('addEquipment.class') || 'Klasa sprzętu'}</Label>
                <TypeaheadWrapper data-equipment-typeahead="true">
                  <Input
                    name="classId"
                    value={form.classId || ''}
                    autoComplete="off"
                    onChange={handleTypeaheadChange}
                    onFocus={(e) => updateTypeahead('classId', e.target.value)}
                    data-error={!!fieldErrors.classId}
                    placeholder={t('addEquipment.classPlaceholder') || 'Wpisz klasę sprzętu'}
                  />
                  {renderTypeahead('classId')}
                </TypeaheadWrapper>
              </Field>
            </FieldGrid>

            <FieldGrid>
              <Field>
                <Label>{t('addEquipment.price') || 'Cena za dzień (PLN)'}</Label>
                <Input name="price" type="number" step="0.01" value={form.price || ''} onChange={handleChange} onWheel={e => e.target.blur()} data-error={!!fieldErrors.price} />
              </Field>
              <Field>
                <Label>{t('addEquipment.priceOver7') || 'Cena za dzień (> 7 dni)'}</Label>
                <Input name="price_over_7" type="number" step="0.01" value={form.price_over_7 || ''} onChange={handleChange} onWheel={e => e.target.blur()} />
              </Field>
              <Field>
                <Label>{t('addEquipment.bail') || 'Kaucja'}</Label>
                <Input name="bail" type="number" step="0.01" value={form.bail || ''} onChange={handleChange} onWheel={e => e.target.blur()} data-error={!!fieldErrors.bail} />
              </Field>
              <Field>
                <Label>{t('inventory.status') || 'Status'}</Label>
                <StatusBadge data-status={selectedStatus?.toLowerCase()}>
                  <DotIcon />
                  {equipmentStatusLabel(t, selectedStatus)}
                </StatusBadge>
              </Field>
            </FieldGrid>

            {dynamicFieldKeys.length > 0 && (
              <FieldGrid>
                {dynamicFieldKeys.map((key) => {
                  const wlasne = customFieldDefByKey.get(key);
                  const config = DETAIL_FIELD_CONFIG_BY_KEY[key]
                    || (wlasne ? { key, type: wlasne.fieldType === 'number' ? 'number' : (wlasne.fieldType === 'date' ? 'date' : 'text') } : { key, type: 'text' });
                  // Etykieta kolumny własnej jest LITERALNA (nadała ją firma) i wygrywa z
                  // `humanizeFieldKey`, które z `frame_number` robiło „frame number".
                  const rawLabel = wlasne
                    ? (wlasne.label || key)
                    : (config.labelKey ? (t(config.labelKey) || humanizeFieldKey(key)) : humanizeFieldKey(key));
                  const fieldValue = getFieldValue(form, key);
                  const inputType = config.type === 'number' ? 'number' : (config.type === 'date' ? 'date' : 'text');
                  const isNumber = inputType === 'number';

                  // special edit-mode UI for currency: autocomplete with flags
                  if (normalizeDetailFieldKey(key) === 'currency') {
                    return (
                      <Field key={key}>
                        <Label>{rawLabel}</Label>
                        <div style={{ position: 'relative', width: '100%' }} ref={currencyContainerRef}>
                          <Input
                            style={{ width: '100%' }}
                            name={key}
                            autoComplete="off"
                            value={fieldValue || ''}
                            onChange={e => {
                              handleChange(e);
                              const q = String(e.target.value || '').trim().toUpperCase();
                              if (!q) { setCurrencySuggestions([]); setShowCurrencySuggestions(false); return; }
                              if (!currencies.length) fetchCurrencies();
                              const list = (currencies || []).filter(c => c.code.startsWith(q) || c.name.toLowerCase().includes(q.toLowerCase()));
                              setCurrencySuggestions(list.slice(0, 12));
                              setShowCurrencySuggestions(true);
                            }}
                            onFocus={() => {
                              if (!currencies.length) fetchCurrencies();
                              const q = String(fieldValue || '').trim().toUpperCase();
                              const list = (currencies || []).filter(c => !q || c.code.startsWith(q) || c.name.toLowerCase().includes(q.toLowerCase()));
                              setCurrencySuggestions(list.slice(0, 12));
                              setShowCurrencySuggestions(true);
                            }}
                            placeholder={t('addEquipment.currencyPlaceholder') || 'np. PLN'}
                            data-error={!!fieldErrors[key]}
                          />

                          {showCurrencySuggestions && (currencySuggestions.length > 0 || currenciesLoading) && (
                            <div style={{
                              position: 'absolute',
                              zIndex: 'var(--z-dropdown)',
                              left: 0,
                              right: 0,
                              background: 'var(--card-bg)',
                              border: '1px solid var(--input-border)',
                              boxShadow: 'var(--shadow-md)',
                              maxHeight: 260,
                              overflow: 'auto'
                            }}>
                              {currenciesLoading && <div style={{ padding: 8, color: 'var(--text-muted)' }}>{t('addEquipment.currencyLoading')}</div>}
                              {!currenciesLoading && currencySuggestions.map(c => (
                                <div key={c.code} onMouseDown={ev => ev.preventDefault()} onClick={() => {
                                  setForm(prev => ({ ...prev, currency: c.code }));
                                  setShowCurrencySuggestions(false);
                                  setCurrencySuggestions([]);
                                  setFieldErrors(prev => ({ ...prev, currency: false }));
                                }} style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', cursor: 'pointer' }}>
                                  {c.flag && <img src={c.flag} alt={c.code} style={{ width: 20, height: 14, objectFit: 'cover', marginRight: 8 }} />}
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontWeight: 600 }}>{c.code}</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.name}</div>
                                  </div>
                                </div>
                              ))}
                              {!currenciesLoading && currencySuggestions.length === 0 && (
                                <div style={{ padding: 8, color: 'var(--text-muted)' }}>{t('addEquipment.currencyNoMatches')}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </Field>
                    );
                  }

                  return (
                    <Field key={key}>
                      <Label>{rawLabel}</Label>
                      {inputType === 'text' && ['location', 'supplier'].includes(normalizeDetailFieldKey(key)) ? (
                        <TypeaheadWrapper data-equipment-typeahead="true">
                          <Input
                            name={key}
                            type={inputType}
                            autoComplete="off"
                            value={fieldValue || ''}
                            onChange={handleTypeaheadChange}
                            onFocus={(e) => updateTypeahead(normalizeDetailFieldKey(key), e.target.value)}
                            data-error={!!fieldErrors[key]}
                          />
                          {renderTypeahead(normalizeDetailFieldKey(key))}
                        </TypeaheadWrapper>
                      ) : (
                        <Input
                          name={key}
                          type={inputType}
                          step={isNumber ? '0.01' : undefined}
                          autoComplete={inputType === 'text' ? 'off' : undefined}
                          value={fieldValue || ''}
                          onChange={handleChange}
                          onWheel={isNumber ? (e => e.target.blur()) : undefined}
                          data-error={!!fieldErrors[key]}
                        />
                      )}
                    </Field>
                  );
                })}
              </FieldGrid>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, margin: '4px 0 12px' }}>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>{t('equipmentDetails.scanHint')}</span>
              <ScanSources onScan={handleScanFill} compact dataCyPrefix="equipment-scan" />
            </div>

            <ButtonRow>
              {/* minWidth 190 na obu: Zapisz zmienia napis w trakcie zapisu ("Zapisz" ->
                  "Zapisywanie..."), wiec bez rezerwacji szerokosci rosnie i wraca, czyli skacze
                  pod kursorem — Anuluj dostaje ta sama szerokosc, zeby oba przyciski w parze byly
                  rowne, a nie jeden dominujacy i jeden przypadkowo mniejszy. Edytuj/Kalendarz w
                  widoku podgladu (osobny ButtonRow nizej) maja stale napisy i wlasny min-width
                  140px z bazowego stylu — nie dotyczy ich. Znalezione w code review PR #132. */}
              <PrimaryButton onClick={handleSave} disabled={loading} style={{ minWidth: 190 }}>
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <LoadingSpinner style={{ margin: 0, width: 16, height: 16, borderTopColor: 'white' }} />
                    <span>{t('common.saving') || 'Zapisywanie...'}</span>
                  </div>
                ) : (
                  <>
                    <SaveIcon /> {t('equipmentDetails.save') || 'Zapisz'}
                  </>
                )}
              </PrimaryButton>
              <SecondaryButton onClick={handleCancel} style={{ minWidth: 190 }}>
                <XIcon size={16} /> {t('equipmentDetails.cancel') || 'Anuluj'}
              </SecondaryButton>
            </ButtonRow>
          </>
        ) : (
          <>
            <FieldGrid>
              <Field>
                <Label>{t('addEquipment.category') || 'Kategoria sprzętu'}</Label>
                <Value className="readonly">{typeLabel(t, equipment.category)}</Value>
              </Field>
              <Field>
                <Label>{t('addEquipment.brand') || 'Marka'}</Label>
                <Value className="readonly">{equipment.brand || '—'}</Value>
              </Field>
              <Field>
                <Label>{t('addEquipment.name') || 'Nazwa'}</Label>
                <Value className="readonly">{equipment.name}</Value>
              </Field>
              <Field>
                <Label>{t('addEquipment.size') || 'Rozmiar'}</Label>
                <Value className="readonly">{equipment.size || '—'}</Value>
              </Field>
              <Field>
                <Label>{t('addEquipment.inventoryNumber') || 'Numer'}</Label>
                <Value className="readonly">{equipment.inventory_number || '—'}</Value>
                {equipment.metadata?.previous_inventory_number && (
                  <PreviousNumber data-cy="equipment-previous-number">
                    {t('equipmentNumber.previous').replace('{number}', equipment.metadata.previous_inventory_number)}
                  </PreviousNumber>
                )}
              </Field>
              <Field>
                <Label>{t('addEquipment.class') || 'Klasa sprzętu'}</Label>
                <Value className="readonly">{classLabel(t, equipment.class) || '-'}</Value>
              </Field>
            </FieldGrid>

            {/* zachowaj pola cenowe i kaucję widoczne pod głównym blokiem */}
            <FieldGrid>
              <Field>
                <Label>{t('addEquipment.price') || 'Cena za dzień (PLN)'}</Label>
                <Value className="readonly">{
                  (() => {
                    const num = Number(equipment.price || 0);
                    const cur = String(getFieldValue(equipment, 'currency') || '').toUpperCase();
                    const formatted = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
                    const curObj = (currencies || []).find(c => c.code === cur);
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span>{formatted}</span>
                            {cur ? (<span style={{ fontWeight: 600 }}>{cur}</span>) : null}
                      </span>
                    );
                  })()
                }</Value>
              </Field>
              <Field>
                <Label>{t('addEquipment.priceOver7') || 'Cena za dzień (> 7 dni)'}</Label>
                <Value className="readonly">{
                  (() => {
                    const num = Number(equipment.price_over_7 || 0);
                    const cur = String(getFieldValue(equipment, 'currency') || '').toUpperCase();
                    const formatted = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
                    const curObj = (currencies || []).find(c => c.code === cur);
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span>{formatted}</span>
                        {cur ? (<span style={{ fontWeight: 600 }}>{cur}</span>) : null}
                      </span>
                    );
                  })()
                }</Value>
              </Field>
              <Field>
                <Label>{t('addEquipment.bail') || 'Kaucja'}</Label>
                <Value className="readonly">{
                  (() => {
                    const num = Number(equipment.bail || 0);
                    const cur = String(getFieldValue(equipment, 'currency') || '').toUpperCase();
                    const formatted = new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
                    const curObj = (currencies || []).find(c => c.code === cur);
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span>{formatted}</span>
                        {cur ? (<span style={{ fontWeight: 600 }}>{cur}</span>) : null}
                      </span>
                    );
                  })()
                }</Value>
              </Field>
              <Field>
                <Label>{t('inventory.status') || 'Status'}</Label>
                <StatusBadge data-status={equipment.status?.toLowerCase()}>
                  <DotIcon />
                  {equipmentStatusLabel(t, equipment.status)}
                </StatusBadge>
              </Field>
            </FieldGrid>

            {dynamicFieldKeys.length > 0 && (
              <FieldGrid>
                {dynamicFieldKeys.map((key) => {
                  const wlasne = customFieldDefByKey.get(key);
                  const config = DETAIL_FIELD_CONFIG_BY_KEY[key] || { key };
                  const rawLabel = wlasne
                    ? (wlasne.label || key)
                    : (config.labelKey ? (t(config.labelKey) || humanizeFieldKey(key)) : humanizeFieldKey(key));
                  const value = getFieldValue(equipment, key);
                  // special rendering for currency: show flag + code + name
                  if (normalizeDetailFieldKey(key) === 'currency') {
                    const cur = String(value || '').toUpperCase();
                    const curObj = (currencies || []).find(c => c.code === cur);
                    return (
                      <Field key={key}>
                        <Label>{rawLabel}</Label>
                        <Value className="readonly">
                          {cur ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {curObj && curObj.flag ? <img src={curObj.flag} alt={cur} style={{ width: 20, height: 14, objectFit: 'cover' }} /> : null}
                              <span style={{ fontWeight: 600 }}>{cur}</span>
                              {curObj && curObj.name ? <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{curObj.name}</span> : null}
                            </span>
                          ) : '-'}
                        </Value>
                      </Field>
                    );
                  }

                  return (
                    <Field key={key}>
                      <Label>{rawLabel}</Label>
                      <Value className="readonly">{hasMeaningfulValue(value) ? String(value) : '-'}</Value>
                    </Field>
                  );
                })}
              </FieldGrid>
            )}
            <ButtonRow>
              <PrimaryButton onClick={() => setEdit(true)}>
                <EditIcon /> {t('equipmentDetails.edit') || 'Edytuj'}
              </PrimaryButton>
              <PrimaryButton onClick={() => navigate(`/inventory/${id}/calendar`)}>
                <CalendarIcon /> {t('equipmentDetails.calendar') || 'Kalendarz'}
              </PrimaryButton>
              <PrimaryButton onClick={() => navigate(`/inventory/labels?ids=${id}`)} data-cy="equipment-print-label">
                <PrinterIcon size={16} /> {t('labels.printOne')}
              </PrimaryButton>
            </ButtonRow>
          </>
        )}
      </DetailPanel>
    </Container>
  );
}

// Dodane style dla nowych komponentów formularza

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
  
  &:disabled,
  &.readonly {
    background: var(--panel-bg);
    opacity: 0.7;
    cursor: not-allowed;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
`;

const TypeaheadWrapper = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const TypeaheadList = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: var(--z-dropdown);
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  max-height: 240px;
  overflow-y: auto;
  padding: 6px;
`;

const TypeaheadItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(139, 92, 246, 0.08);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
  
  &:disabled {
    background: var(--panel-bg);
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid var(--input-border);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }
`;

const ErrorMessage = styled.div`
  padding: 20px;
  background: var(--danger-bg);
  border: 2px solid var(--danger-border);
  border-radius: 12px;
  color: var(--danger);
  text-align: center;
  font-weight: 500;
  margin: 20px;
`;

const Value = styled.div`
  padding: 12px 16px;
  background: var(--panel-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 500;
  min-height: 44px;
  display: flex;
  align-items: center;
`;

const ReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  
  &[data-status="dostępny"], &[data-status="available"] {
    background: var(--success-bg);
    color: var(--success);
    border: 1px solid var(--success-border);
  }

  &[data-status="wypożyczony"], &[data-status="rented"] {
    background: color-mix(in srgb, var(--primary) 15%, transparent);
    color: var(--primary);
    border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
  }

  &[data-status="w serwisie"], &[data-status="in service"] {
    background: var(--warning-bg);
    color: var(--warning);
    border: 1px solid var(--warning-border);
  }

  &[data-status="nieaktywny"], &[data-status="inactive"] {
    background: color-mix(in srgb, var(--text-muted) 15%, transparent);
    color: var(--text-muted);
    border: 1px solid color-mix(in srgb, var(--text-muted) 30%, transparent);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 32px;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: var(--primary-contrast);
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-width: 140px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: var(--card-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-width: 140px;
  
  &:hover {
    background: var(--hover-bg);
    border-color: var(--primary);
    color: var(--primary);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  
  &:hover {
    border-color: var(--primary);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
  
  &[data-placeholder="true"] {
    color: var(--text-muted);
  }
  
  &[data-open="true"] {
    border-color: var(--primary);
  }
`;

const FilterOptionsList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--card-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  z-index: var(--z-popover);
  max-height: 300px;
  overflow-y: auto;
  animation: ${slideIn} 0.2s ease-out;
`;

const FilterOptionItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: var(--text);
  
  .option-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .option-label {
    flex: 1;
  }
  
  &:hover {
    background: var(--hover-bg);
  }
  
  &[data-selected="true"] {
    background: rgba(139, 92, 246, 0.1);
    color: var(--primary);
    font-weight: 600;
  }
  
  &:first-child {
    border-radius: 10px 10px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 10px 10px;
  }
`;

const DETAIL_FIELD_CONFIGS = [
  { key: 'size', labelKey: 'addEquipment.size', type: 'text' },
  { key: 'price_over_7', labelKey: 'addEquipment.priceOver7', type: 'number' },
  { key: 'condition', labelKey: 'addEquipment.condition', type: 'text' },
  { key: 'location', labelKey: 'addEquipment.location', type: 'text' },
  { key: 'tags', labelKey: 'addEquipment.tags', type: 'text' },
  { key: 'supplier', labelKey: 'addEquipment.supplier', type: 'text' },
  { key: 'purchaseDate', labelKey: 'addEquipment.purchaseDate', type: 'date', aliases: ['purchase_date'] },
  { key: 'maintenanceDate', labelKey: 'addEquipment.maintenanceDate', type: 'date', aliases: ['maintenance_date'] },
  { key: 'images', labelKey: 'addEquipment.images', type: 'text' },
  { key: 'barcode', labelKey: 'addEquipment.barcode', type: 'text' },
  { key: 'currency', labelKey: 'addEquipment.currency', type: 'text', aliases: ['price_currency','deposit_currency','priceCurrency','depositCurrency','currency'] },
  { key: 'wearLevel', labelKey: 'addEquipment.wearLevel', type: 'text', aliases: ['wear_level'] },
  { key: 'compatibility', labelKey: 'addEquipment.compatibility', type: 'text' },
  { key: 'sizeRange', labelKey: 'addEquipment.sizeRange', type: 'text', aliases: ['size_range'] },
  { key: 'sku', labelKey: 'addEquipment.sku', type: 'text' }
];

const DETAIL_FIELD_CONFIG_BY_KEY = DETAIL_FIELD_CONFIGS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

const DETAIL_FIELD_ALIAS_TO_KEY = DETAIL_FIELD_CONFIGS.reduce((acc, item) => {
  acc[item.key] = item.key;
  (item.aliases || []).forEach(alias => {
    acc[alias] = item.key;
  });
  return acc;
}, {});

// Poprzedni numer sztuki, której numer przeniesiono na inny sprzęt (24.10.2026).
const PreviousNumber = styled.p`
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: var(--text-muted);
`;

const excludedDynamicFields = new Set([
  'id',
  'name',
  'brand',
  'size',
  'category',
  'metadata',
  'class',
  'classId',
  'class_id',
  'typeId',
  'price',
  'price_over_7',
  'bail',
  'status',
  'statusId',
  'status_id',
  'inventory_number',
  // Numer przeniesiony na inną sztukę (lib/numerPoNieaktywnym.js): pokazywany pod numerem, nie jako pole do edycji.
  'previous_inventory_number',
  'created_at',
  'updated_at'
]);

// currency list for flags/names
const DEFAULT_CURRENCY_API = 'https://restcountries.com/v3.1/all?fields=flags,currencies';


function loadInventorySettings() {
  if (typeof window === 'undefined') {
    return { visibleColumns: [], categoryRequirements: {}, customColumns: [] };
  }
  try {
    const raw = window.localStorage.getItem(getInventorySettingsStorageKey());
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      visibleColumns: Array.isArray(parsed?.visibleColumns) ? parsed.visibleColumns : [],
      categoryRequirements: parsed?.categoryRequirements || {},
      // Kolumny własne (E.2). Bez nich edycja pokazywała pole własne TYLKO wtedy, gdy sztuka
      // już miała w nim wartość (`usedEquipmentFields`) — pustego nie dało się uzupełnić,
      // a etykietą był wtedy zmielony klucz („frame number") zamiast nazwy nadanej przez firmę.
      customColumns: (Array.isArray(parsed?.customColumns) ? parsed.customColumns : []).filter(c => c && c.key)
    };
  } catch {
    return { visibleColumns: [], categoryRequirements: {}, customColumns: [] };
  }
}

function hasMeaningfulValue(value) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'boolean') return true;
  return Boolean(value);
}

function normalizeDetailFieldKey(key) {
  return DETAIL_FIELD_ALIAS_TO_KEY[key] || key;
}

function humanizeFieldKey(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, m => m.toUpperCase());
}

function getFieldValue(source, key) {
  if (!source || !key) return '';
  const config = DETAIL_FIELD_CONFIG_BY_KEY[key];
  if (Object.prototype.hasOwnProperty.call(source, key)) return source[key] ?? '';
  const aliases = config?.aliases || [];
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(source, alias)) return source[alias] ?? '';
  }
  // if not found on top-level, check metadata object
  const meta = source && source.metadata && typeof source.metadata === 'object' ? source.metadata : null;
  if (meta) {
    if (Object.prototype.hasOwnProperty.call(meta, key)) return meta[key] ?? '';
    for (const alias of aliases) {
      if (Object.prototype.hasOwnProperty.call(meta, alias)) return meta[alias] ?? '';
    }
  }
  return '';
}

// currency helper state + fetch
function useCurrencyList() {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCurrencies = async () => {
    if (currencies.length || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(DEFAULT_CURRENCY_API);
      const rows = res.data || [];
      const map = {};
      rows.forEach(r => {
        const flag = r.flags && (r.flags.svg || r.flags.png) ? (r.flags.svg || r.flags.png) : '';
        const cur = r.currencies || {};
        Object.keys(cur).forEach(code => {
          if (!code) return;
          if (!map[code]) map[code] = { code, name: (cur[code] && cur[code].name) || code, flag };
        });
      });
      const list = Object.values(map).sort((a,b) => a.code.localeCompare(b.code));
      setCurrencies(list);
    } catch (err) {
      console.error('fetch currencies', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCurrencies(); }, []);
  return { currencies, loading, fetchCurrencies };
}